import { useMemo } from "react";
import { Clock } from "lucide-react";

/**
 * Per-section (per item.rule) timing + description editor. Sections are
 * derived from the current items list — this only lets the admin attach
 * metadata (label / description / Maximum time / Expert time) to rule
 * values that already exist, it doesn't create or rename items.
 *
 * `section_meta` is stored on the module doc as { [rule]: { label, description,
 * target_seconds, expert_seconds } } and is consumed by post_module_attempt
 * (server-authoritative target for section attempts) and by the student
 * Test screen (display + informational Expert-time hint).
 *
 * `description` is stored as { en, ta, ur, ms, sw } — a locale-keyed dict
 * so the student sees the description in their chosen language. Old plain-
 * string values from before this change are handled gracefully on the student
 * side with a typeof guard.
 */

export default function SectionTimingsEditor({ m, update, locale }) {
  const sections = useMemo(() => {
    const seen = new Set();
    const order = [];
    for (const it of m.items || []) {
      const r = it.rule;
      if (r && !seen.has(r)) { seen.add(r); order.push(r); }
    }
    return order;
  }, [m.items]);

  const sectionMeta = m.section_meta || {};

  const setField = (rule, field, value) => {
    update("section_meta", {
      ...sectionMeta,
      [rule]: { ...(sectionMeta[rule] || {}), [field]: value },
    });
  };

  const setDescription = (rule, value) => {
    const existing = sectionMeta[rule]?.description;
    const currentDict = (existing && typeof existing === "object") ? existing : {};
    setField(rule, "description", { ...currentDict, [locale]: value });
  };

  const getDescription = (rule) => {
    const desc = sectionMeta[rule]?.description;
    if (!desc) return "";
    if (typeof desc === "string") return locale === "en" ? desc : "";
    return desc[locale] || "";
  };

  const hasDescription = (rule) => {
    const desc = sectionMeta[rule]?.description;
    if (!desc) return false;
    if (typeof desc === "string") return desc.trim().length > 0;
    return Object.values(desc).some((v) => (v || "").trim().length > 0);
  };

  if (sections.length === 0) {
    return (
      <div className="text-sm text-[#4B5563] py-4 text-center">
        No sections yet — sections come from the "Rule" column on the Reading items tab. Add rule tags there first (e.g. "Sukoon", "Shaddah"), then come back here to set their timing.
      </div>
    );
  }

  return (
    <div data-testid="section-timings-editor">
      <div className="text-sm text-[#4B5563] mb-3">
        Each section is independently tested in Test mode. Maximum time is the pass/fail cutoff; Expert time is shown as a stretch goal and does not affect pass/fail. Description is shown to students above this section's items.
      </div>

      <div className="rounded-xl border border-[#E7E0D2] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[#4B5563] text-xs uppercase tracking-[0.16em]">
            <tr>
              <th className="px-3 py-2 text-left">Rule</th>
              <th className="px-3 py-2 text-left">Description ({locale.toUpperCase()})</th>
              <th className="px-3 py-2 text-right w-32">Maximum (s)</th>
              <th className="px-3 py-2 text-right w-32">Expert (s)</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((rule) => {
              const meta = sectionMeta[rule] || {};
              const count = (m.items || []).filter((it) => it.rule === rule).length;
              return (
                <tr key={rule} className="border-t border-[#E7E0D2]" data-testid={`section-timing-row-${rule}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-[#1F2937]">{rule}</div>
                    <div className="text-xs text-[#9CA3AF]">{count} items</div>
                    {hasDescription(rule) && <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-[#10B981]" title="Has description" />}
                  </td>
                  <td className="px-3 py-2">
                    <textarea
                      data-testid={`section-desc-${rule}`}
                      rows={2}
                      value={getDescription(rule)}
                      onChange={(e) => setDescription(rule, e.target.value)}
                      placeholder={`Description in ${locale.toUpperCase()}…`}
                      className="w-full px-2 py-1.5 rounded-lg bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm"
                    />
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <input
                      data-testid={`section-max-${rule}`}
                      type="number" min={0}
                      value={meta.target_seconds ?? ""}
                      onChange={(e) => setField(rule, "target_seconds", e.target.value === "" ? null : Number(e.target.value))}
                      className="w-24 px-2 py-1.5 rounded-lg bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm text-right tabular-nums"
                    />
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    <input
                      data-testid={`section-expert-${rule}`}
                      type="number" min={0}
                      value={meta.expert_seconds ?? ""}
                      onChange={(e) => setField(rule, "expert_seconds", e.target.value === "" ? null : Number(e.target.value))}
                      className="w-24 px-2 py-1.5 rounded-lg bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm text-right tabular-nums"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-[#9CA3AF]">
        <Clock className="w-3.5 h-3.5" /> Leave Maximum time blank to fall back to a proportional split of the module's overall Maximum time.
      </div>
    </div>
  );
}
